import {
  charts_categorical1,
  charts_categorical2,
  charts_categorical3,
} from 'ext-ui/style/colors'
import React, { useMemo } from 'react'
import ChartQuestionPopover from 'src/agent/components/ChartQuestionPopover/ChartQuestionPopover'
import type { AskReportingChartQuestion } from 'src/agent/reporting/chartQuestion.types'
import VisxStackedBarChart from 'src/charting/renderers/visx/VisxStackedBarChart'
import {
  firstPresentMetadataValue,
  getPointDisplayLabel,
} from 'src/charting/reporting/reportingChartData'
import { toReportingStackedBarPlan } from 'src/charting/reporting/reportingStackedBarPlan'
import { ConfigContext } from 'src/context/ConfigContext/ConfigContextBase'
import { useTranslate } from 'src/localization/Trns'
import { useQuerySpendChart } from 'src/queries/reporting/domain/useQuerySpendChart'
import {
  SpendChart,
  SpendChartPoint,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'

import * as style from '../Reporting.style'
import {
  formatCompactMoney,
  formatDate,
  formatPercentage,
  getTopSeriesStat,
  hasSpendData,
  isUnattributedSeriesKey,
  PanelGatedState,
  ReportingPanelQueryParams,
} from './reportingPanelUtils'

interface SpendStackedBarPanelProps extends ReportingPanelQueryParams {
  chartId: SpendChart
  title: string
  description: string
  summaryLabel?: string
  emptyMessage?: string
  seriesLabelForPoint?: (point: SpendChartPoint) => string
  seriesLabelFormat?: (seriesKey: string, fallbackLabel: string) => string
  shouldHoldForLabels?: (chart: SpendChartView | undefined) => boolean
  isLabelHydrationLoading?: boolean
  isLabelHydrationError?: boolean
  onRetryLabels?: () => void
  gatedState?: PanelGatedState
  isAdmin?: boolean
  onAskQuestion?: AskReportingChartQuestion
  questionContextLabel?: string
}

const skeletonBars = [55, 82, 38, 72, 48, 88, 63]

const LoadingState: React.FC = () => {
  const translate = useTranslate()

  return (
    <div css={style.chartSkeleton} aria-label={translate('Loading chart data')}>
      {skeletonBars.map((height, index) => (
        <span
          key={height + index}
          css={style.chartSkeletonBar}
          style={{ height: `${height}%` }}
          aria-hidden="true"
        />
      ))}
    </div>
  )
}

const MessageState: React.FC<{
  message: string
  detail?: string
  retryLabel?: string
  onRetry?: () => void
}> = ({ message, detail, retryLabel, onRetry }) => (
  <div css={style.panelMessage}>
    <p>{message}</p>
    {detail ? <span css={style.panelMessageDetail}>{detail}</span> : null}
    {retryLabel && onRetry ? (
      <button type="button" css={style.retryButton} onClick={onRetry}>
        {retryLabel}
      </button>
    ) : null}
  </div>
)

interface GatedStateProps {
  type: PanelGatedState['type']
  isAdmin: boolean
  destination?: string
  onNavigate?: (destination: string) => void
}

const GatedState: React.FC<GatedStateProps> = ({
  type,
  isAdmin,
  destination,
  onNavigate,
}) => {
  const translate = useTranslate()

  const message =
    type === 'feature_disabled'
      ? translate('See spend broken down by team.')
      : type === 'not_configured'
        ? translate('Set up teams to see this feature.')
        : type === 'not_connected'
          ? translate('See spend by GL category.')
          : translate('Account breakdown is a Pro feature.')

  const adminLabel =
    type === 'feature_disabled'
      ? translate('Turn on Departments')
      : type === 'not_configured'
        ? translate('Configure Teams')
        : type === 'not_connected'
          ? translate('Connect your ERP')
          : translate('Upgrade')

  const memberSubcopy =
    type === 'not_entitled'
      ? translate('Ask your workspace admin to upgrade.')
      : type === 'not_configured'
        ? translate('Ask your workspace admin to configure this.')
        : translate('Ask your workspace admin to enable this.')

  const body = (
    <div css={style.panelMessage}>
      <p>{message}</p>
      {isAdmin ? (
        <button
          type="button"
          css={style.gatedButton}
          onClick={() => destination && onNavigate?.(destination)}
        >
          {adminLabel}
        </button>
      ) : (
        <span css={style.memberSubcopy}>
          {/* lock-new icon (DS icon, aria-hidden, currentColor inherits grey500 from memberSubcopy) */}
          <svg
            width="11"
            height="12"
            viewBox="0 0 14.8333 16.5"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M11.5833 6.58333V4.91667C11.5833 2.61548 9.71785 0.75 7.41667 0.75C5.11548 0.75 3.25 2.61548 3.25 4.91667V6.58333M7.41667 10.3333V12M4.75 15.75H10.0833C11.4835 15.75 12.1835 15.75 12.7183 15.4775C13.1887 15.2378 13.5712 14.8554 13.8108 14.385C14.0833 13.8502 14.0833 13.1501 14.0833 11.75V10.5833C14.0833 9.1832 14.0833 8.48314 13.8108 7.94836C13.5712 7.47795 13.1887 7.0955 12.7183 6.85582C12.1835 6.58333 11.4835 6.58333 10.0833 6.58333H4.75C3.34987 6.58333 2.6498 6.58333 2.11502 6.85582C1.64462 7.0955 1.26217 7.47795 1.02248 7.94836C0.750001 8.48314 0.750001 9.1832 0.750001 10.5833V11.75C0.750001 13.1501 0.750001 13.8502 1.02248 14.385C1.26217 14.8554 1.64462 15.2378 2.11502 15.4775C2.6498 15.75 3.34987 15.75 4.75 15.75Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {memberSubcopy}
        </span>
      )}
    </div>
  )

  return (
    <div css={style.gateTeaser}>
      {/* Illustrative aria-hidden sample bars — never real data */}
      <div css={style.teaserChart} aria-hidden="true">
        <div
          style={{
            flex: 1,
            height: '60%',
            background: charts_categorical1,
            borderRadius: '4px 4px 0 0',
            alignSelf: 'flex-end',
          }}
        />
        <div
          style={{
            flex: 1,
            height: '85%',
            background: charts_categorical2,
            borderRadius: '4px 4px 0 0',
            alignSelf: 'flex-end',
          }}
        />
        <div
          style={{
            flex: 1,
            height: '45%',
            background: charts_categorical3,
            borderRadius: '4px 4px 0 0',
            alignSelf: 'flex-end',
          }}
        />
        <div
          style={{
            flex: 1,
            height: '70%',
            background: charts_categorical1,
            borderRadius: '4px 4px 0 0',
            alignSelf: 'flex-end',
          }}
        />
        <div
          style={{
            flex: 1,
            height: '55%',
            background: charts_categorical2,
            borderRadius: '4px 4px 0 0',
            alignSelf: 'flex-end',
          }}
        />
        <div
          style={{
            flex: 1,
            height: '80%',
            background: charts_categorical3,
            borderRadius: '4px 4px 0 0',
            alignSelf: 'flex-end',
          }}
        />
      </div>
      <div css={style.teaserOverlay}>{body}</div>
    </div>
  )
}

const SpendStackedBarPanel: React.FC<SpendStackedBarPanelProps> = ({
  chartId,
  title,
  description,
  summaryLabel,
  emptyMessage,
  organizationId,
  currency,
  enabled,
  dateRange,
  seriesLabelForPoint,
  seriesLabelFormat,
  shouldHoldForLabels,
  isLabelHydrationLoading,
  isLabelHydrationError,
  onRetryLabels,
  gatedState,
  isAdmin,
  onAskQuestion,
  questionContextLabel,
  onNavigate,
  resolveGatedDestination,
}) => {
  const translate = useTranslate()
  const { locale } = React.useContext(ConfigContext)
  const query = useQuerySpendChart(
    {
      organizationId,
      chart: chartId,
      since: formatDate(dateRange.since),
      until: formatDate(dateRange.until),
      currency,
    },
    enabled,
  )

  const plan = useMemo(() => {
    if (!query.data?.chart || !hasSpendData(query.data.chart)) return undefined

    return toReportingStackedBarPlan({
      chart: query.data.chart,
      title,
      description,
      ariaLabel: translate('${title} chart', { title: translate(title) }),
      unknownLabel: translate('Unknown'),
      seriesLabelForPoint,
    })
  }, [description, query.data?.chart, seriesLabelForPoint, title, translate])

  const totalSpend = query.data?.chart.totalSpendCents ?? 0
  const holdForLabels = shouldHoldForLabels?.(query.data?.chart) ?? false
  const isLoading =
    query.isLoading || (holdForLabels && isLabelHydrationLoading === true)
  const isError =
    query.isError || (holdForLabels && isLabelHydrationError === true)
  const retry = () => {
    if (query.isError) query.refetch()
    if (holdForLabels && isLabelHydrationError) onRetryLabels?.()
  }

  const totalCompanyBreakdown = useMemo(() => {
    if (chartId !== 'total_company_spend' || !query.data?.chart) {
      return undefined
    }

    const totalsBySeries = query.data.chart.points.reduce<
      Record<string, number>
    >((acc, point) => {
      const rawSeries =
        firstPresentMetadataValue(point, ['cardType', 'seriesLabel']) ??
        point.seriesLabel ??
        point.label
      const series = rawSeries.toLowerCase().includes('physical')
        ? 'Physical'
        : 'Virtual'
      acc[series] = (acc[series] ?? 0) + point.spendCents
      return acc
    }, {})

    const total = (totalsBySeries.Physical ?? 0) + (totalsBySeries.Virtual ?? 0)
    const physicalShare =
      total === 0 ? 0 : (totalsBySeries.Physical ?? 0) / total
    const virtualShare = total === 0 ? 0 : (totalsBySeries.Virtual ?? 0) / total

    return translate('${physical} / ${virtual}', {
      physical: formatPercentage(physicalShare, locale),
      virtual: formatPercentage(virtualShare, locale),
    })
  }, [chartId, locale, query.data?.chart, translate])

  // Exclude the synthetic "Uncategorized"/"Unknown_*" buckets from the headline,
  // keyed off the stable API seriesKey (locale-proof) and resolved to the same
  // display label the plan uses, so neither can ever be the "Top X".
  const excludedSeriesLabels = useMemo(() => {
    const labels = new Set<string>()
    for (const point of query.data?.chart.points ?? []) {
      if (isUnattributedSeriesKey(point.seriesKey)) {
        labels.add(
          seriesLabelForPoint?.(point) ??
            getPointDisplayLabel(point, translate('Unknown'), [
              'seriesLabel',
              'label',
              'name',
              'displayName',
            ]),
        )
      }
    }
    return labels
  }, [query.data?.chart, seriesLabelForPoint, translate])

  const topSeriesStat = useMemo(
    () => getTopSeriesStat(plan, excludedSeriesLabels),
    [plan, excludedSeriesLabels],
  )
  const showTopSeriesStat =
    chartId !== 'total_company_spend' &&
    topSeriesStat !== undefined &&
    !isLoading &&
    !isError

  return (
    <section css={style.chartPanel} aria-labelledby={`${chartId}-title`}>
      <div css={style.panelHeader}>
        <div css={style.panelHeaderTitle}>
          <h2 id={`${chartId}-title`} css={style.chartTitle}>
            {translate(title)}
          </h2>
          {!gatedState &&
            plan &&
            query.data?.analysisContextToken &&
            onAskQuestion &&
            questionContextLabel && (
              <ChartQuestionPopover
                chartTitle={translate(title)}
                contextLabel={questionContextLabel}
                onSubmit={(question): void =>
                  onAskQuestion(
                    {
                      source: 'overview',
                      key: `overview:${chartId}:${formatDate(
                        dateRange.since,
                      )}:${formatDate(dateRange.until)}:${currency}`,
                      title: translate(title),
                      contextLabel: questionContextLabel,
                      plan,
                      currency,
                      analysisContextToken: query.data.analysisContextToken!,
                      query: {
                        chart: chartId,
                        since: formatDate(dateRange.since),
                        until: formatDate(dateRange.until),
                      },
                    },
                    question,
                  )
                }
              />
            )}
          {gatedState?.type === 'not_entitled' && (
            <span css={style.proPill}>{translate('Pro')}</span>
          )}
        </div>
      </div>
      {!gatedState && (
        <div css={style.statsRow}>
          <div css={style.stat}>
            <span css={style.statLabel}>
              {translate(
                showTopSeriesStat
                  ? (summaryLabel ?? 'Top item')
                  : 'Total spend',
              )}
            </span>
            <span css={style.statValue}>
              {showTopSeriesStat && topSeriesStat
                ? `${topSeriesStat.label} · ${formatCompactMoney(
                    topSeriesStat.amountCents,
                    currency,
                    locale,
                  )}`
                : formatCompactMoney(totalSpend, currency, locale)}
            </span>
          </div>
          {totalCompanyBreakdown ? (
            <div css={style.stat}>
              <span css={style.statLabel}>
                {translate('Physical / virtual')}
              </span>
              <span css={style.statValue}>{totalCompanyBreakdown}</span>
            </div>
          ) : null}
        </div>
      )}
      <div css={style.chartBody} data-testid={`reporting-chart-${chartId}`}>
        {gatedState ? (
          <GatedState
            type={gatedState.type}
            isAdmin={isAdmin ?? false}
            destination={resolveGatedDestination?.(
              gatedState.type,
              organizationId,
            )}
            onNavigate={onNavigate}
          />
        ) : isLoading ? (
          <LoadingState />
        ) : isError ? (
          <MessageState
            message={translate('Unable to load chart data.')}
            detail={translate('Try again to refresh this panel.')}
            retryLabel={translate('Retry')}
            onRetry={retry}
          />
        ) : plan ? (
          <VisxStackedBarChart
            plan={plan}
            currency={currency}
            locale={locale}
            showLegend={false}
            seriesLabelFormat={seriesLabelFormat}
          />
        ) : (
          <MessageState
            message={translate(emptyMessage ?? 'No spend in this period.')}
            detail={translate('Try a wider date range.')}
          />
        )}
      </div>
    </section>
  )
}

export default SpendStackedBarPanel
